import { useState } from 'react';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Drawer } from '@/ui/Drawer';
import { Field, Select } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { TabPanel, Tabs } from '@/ui/Tabs';
import { BuscadorDePersonas } from '@/components/BuscadorDePersonas';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { useCursos } from '../api';
import { FormularioDeRuta } from './FormularioDeRuta';
import {
  useAgregarCurso, useBorrarRuta, useCambiarRequerido, useEditarRuta, useGuardarPersonas,
  usePersonasDeLaRuta, useQuitarCurso, useReordenar,
  type PasoDeRuta, type RutaDetalle,
} from './api';

/**
 * GestionDeRuta — el panel de quien gestiona una ruta: sus cursos, sus datos y
 * las personas a las que va.
 *
 * ## Un modal que se sustituía a sí mismo
 *
 * En el vanilla, «Gestionar» abría un `Utils.modal`, y desde él «Editar datos»
 * y «Usuarios específicos» abrían OTRO `Utils.modal` en el mismo
 * `#modal-overlay`: el panel desaparecía y para volver a la secuencia había que
 * cerrar y pulsar «Gestionar» otra vez. Las tres confirmaciones —quitar un
 * curso, archivar, eliminar— eran `Utils.confirm`, que hace lo mismo: quitar un
 * curso cerraba el panel, y al pulsar «Cancelar» en la de archivar el panel ya
 * no estaba.
 *
 * Aquí es un `Drawer` con tres pestañas, y cada confirmación un
 * `ConfirmDialog` con su portal: la pregunta se abre encima y el panel sigue
 * ahí, en la pestaña en que se estaba.
 */
export function GestionDeRuta({
  ruta, abierta, onCerrar,
}: {
  ruta: RutaDetalle;
  abierta: boolean;
  onCerrar: () => void;
}) {
  const [pestana, setPestana] = useState('cursos');
  const editar = useEditarRuta();
  const borrar = useBorrarRuta();
  const [archivando, setArchivando] = useState(false);
  const [borrando, setBorrando] = useState(false);

  function cambiarEstado(status: 'publicado' | 'archivado') {
    editar.mutate(
      { id: ruta.id, cuerpo: { status } },
      {
        onSuccess: () => {
          setArchivando(false);
          Utils.toast(
            status === 'archivado'
              ? 'Ruta archivada'
              : ruta.status === 'archivado' ? 'Ruta restaurada y publicada' : 'Ruta publicada',
            'success',
          );
        },
        onError: (err) => {
          setArchivando(false);
          Utils.toast(err instanceof Error ? err.message : 'No se pudo cambiar el estado', 'error');
        },
      },
    );
  }

  const pie = (
    <div className="flex w-full flex-wrap items-center gap-2">
      {ruta.status !== 'publicado' ? (
        <Button loading={editar.isPending && !archivando} onClick={() => cambiarEstado('publicado')}>
          <i className="bi bi-broadcast" aria-hidden="true" /> {ruta.status === 'archivado' ? 'Restaurar' : 'Publicar'}
        </Button>
      ) : (
        <Button variant="secondary" onClick={() => setArchivando(true)}>
          <i className="bi bi-archive" aria-hidden="true" /> Archivar
        </Button>
      )}
      <Button variant="ghost" onClick={() => setBorrando(true)}>
        <i className="bi bi-trash text-danger-ink" aria-hidden="true" /> Eliminar
      </Button>
      <Button variant="ghost" className="ml-auto" onClick={onCerrar}>Cerrar</Button>
    </div>
  );

  return (
    <>
      <Drawer
        abierto={abierta}
        onCambio={(v) => { if (!v) onCerrar(); }}
        titulo="Gestionar la ruta"
        descripcion={ruta.title}
        ancho="lg"
        pie={pie}
      >
        <Tabs
          valor={pestana}
          onCambio={setPestana}
          pestanas={[
            { clave: 'cursos', etiqueta: `Cursos (${ruta.courses.length})`, icono: 'bi-list-ol' },
            { clave: 'datos', etiqueta: 'Datos', icono: 'bi-sliders' },
            {
              clave: 'personas',
              etiqueta: ruta.target_user_count ? `Personas (${ruta.target_user_count})` : 'Personas',
              icono: 'bi-person-check',
            },
          ]}
        >
          <TabPanel clave="cursos"><Secuencia ruta={ruta} /></TabPanel>
          <TabPanel clave="datos"><FormularioDeRuta ruta={ruta} /></TabPanel>
          <TabPanel clave="personas"><Personas ruta={ruta} /></TabPanel>
        </Tabs>
      </Drawer>

      <ConfirmDialog
        abierto={archivando}
        onCambio={(v) => { if (!v) setArchivando(false); }}
        titulo="Archivar la ruta"
        descripcion="El equipo deja de verla. Sus cursos siguen disponibles por separado, y la ruta se puede restaurar desde el filtro «Archivadas»."
        textoConfirmar="Archivar"
        cargando={editar.isPending}
        onConfirmar={() => cambiarEstado('archivado')}
      />

      <ConfirmDialog
        abierto={borrando}
        onCambio={(v) => { if (!v) setBorrando(false); }}
        titulo="Eliminar la ruta"
        descripcion={`«${ruta.title}» desaparece para todos, también para gestión. Los cursos no se eliminan. Si solo quieres que el equipo deje de verla, archívala.`}
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() =>
          borrar.mutate(ruta.id, {
            onSuccess: () => {
              Utils.toast('Ruta eliminada', 'info');
              location.hash = '#rutas';
            },
            onError: (err) => {
              setBorrando(false);
              Utils.toast(err instanceof Error ? err.message : 'No se pudo eliminar', 'error');
            },
          })
        }
      />
    </>
  );
}

/* ── La secuencia ──────────────────────────────────────────────────────── */

function Secuencia({ ruta }: { ruta: RutaDetalle }) {
  const cursos = useCursos();
  const agregar = useAgregarCurso();
  const requerido = useCambiarRequerido();
  const quitar = useQuitarCurso();
  const reordenar = useReordenar();
  const [elegido, setElegido] = useState('');
  const [esRequerido, setEsRequerido] = useState(true);
  const [quitando, setQuitando] = useState<PasoDeRuta | null>(null);

  const enLaRuta = new Set(ruta.courses.map((c) => c.course_id));
  const disponibles = (cursos.data ?? []).filter((c) => !enLaRuta.has(c.id));
  // El elegido deja de estar disponible en cuanto se añade: se vuelve al primero.
  const seleccion = disponibles.some((c) => String(c.id) === elegido) ? elegido : String(disponibles[0]?.id ?? '');

  function mover(i: number, delta: number) {
    const orden = ruta.courses.map((c) => c.lpc_id);
    const j = i + delta;
    if (j < 0 || j >= orden.length) return;
    [orden[i], orden[j]] = [orden[j]!, orden[i]!];
    reordenar.mutate(
      { id: ruta.id, orden },
      { onError: (err) => Utils.toast(err instanceof Error ? err.message : 'No se pudo reordenar', 'error') },
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <section aria-labelledby="ruta-agregar" className="flex flex-col gap-3 rounded-sm border border-line bg-sunken p-4">
        <h3 id="ruta-agregar" className="text-3 font-bold text-brand-ink">
          <i className="bi bi-plus-circle" aria-hidden="true" /> Añadir un curso
        </h3>
        {cursos.isError ? (
          <EstadoError err={cursos.error} que="los cursos" onReintentar={() => void cursos.refetch()} />
        ) : (
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!seleccion) return;
              agregar.mutate(
                { id: ruta.id, course_id: Number(seleccion), required: esRequerido },
                {
                  onSuccess: () => Utils.toast('Curso añadido al final de la ruta', 'success'),
                  onError: (err) => Utils.toast(err instanceof Error ? err.message : 'No se pudo añadir', 'error'),
                },
              );
            }}
          >
            <Field label="Curso" className="min-w-56 flex-1">
              <Select
                value={seleccion}
                onChange={(e) => setElegido(e.target.value)}
                disabled={cursos.isLoading || !disponibles.length}
              >
                {cursos.isLoading ? (
                  <option>Cargando…</option>
                ) : disponibles.length ? (
                  disponibles.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}{c.status && c.status !== 'publicado' ? ' (borrador)' : ''}
                    </option>
                  ))
                ) : (
                  <option>Todos los cursos ya están en la ruta</option>
                )}
              </Select>
            </Field>
            <label className="flex min-h-11 cursor-pointer items-center gap-2 text-3 text-ink-2">
              <input
                type="checkbox"
                checked={esRequerido}
                onChange={(e) => setEsRequerido(e.target.checked)}
                className="size-4 accent-brand"
              />
              Requerido
            </label>
            <Button type="submit" size="sm" loading={agregar.isPending} disabled={!disponibles.length}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Añadir
            </Button>
          </form>
        )}
      </section>

      <section aria-labelledby="ruta-secuencia" className="flex flex-col gap-2">
        <h3 id="ruta-secuencia" className="text-3 font-bold text-brand-ink">
          <i className="bi bi-list-ol" aria-hidden="true" /> Secuencia ({ruta.courses.length})
        </h3>
        {!ruta.courses.length ? (
          <EstadoVacio
            icono="bi-collection"
            titulo="Sin cursos todavía"
            descripcion="Añade el primero arriba. Cada curso nuevo entra al final."
            compacto
          />
        ) : (
          <ol className="flex flex-col gap-2">
            {ruta.courses.map((c, i) => {
              const cambiandoEste = requerido.isPending && requerido.variables?.lpc_id === c.lpc_id;
              return (
                <li key={c.lpc_id} className="flex flex-wrap items-center gap-2 rounded-sm border border-line bg-surface p-2.5">
                  <span className="w-5 text-center font-bold tabular-nums text-muted" aria-hidden="true">{i + 1}</span>
                  <div className="min-w-40 flex-1">
                    <p className="truncate text-3 font-semibold text-ink" title={c.title}>{c.title}</p>
                    <label className="flex cursor-pointer items-center gap-1.5 text-1 text-muted">
                      <input
                        type="checkbox"
                        checked={c.required}
                        disabled={cambiandoEste}
                        onChange={(e) =>
                          requerido.mutate(
                            { id: ruta.id, lpc_id: c.lpc_id, required: e.target.checked },
                            {
                              onError: (err) =>
                                Utils.toast(err instanceof Error ? err.message : 'No se pudo cambiar', 'error'),
                            },
                          )
                        }
                        className="size-3.5 accent-brand"
                      />
                      Requerido para completar la ruta
                    </label>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    disabled={i === 0 || reordenar.isPending}
                    aria-label={`Subir ${c.title} en la secuencia`}
                    onClick={() => mover(i, -1)}
                  >
                    <i className="bi bi-arrow-up" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    disabled={i === ruta.courses.length - 1 || reordenar.isPending}
                    aria-label={`Bajar ${c.title} en la secuencia`}
                    onClick={() => mover(i, 1)}
                  >
                    <i className="bi bi-arrow-down" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label={`Quitar ${c.title} de la ruta`}
                    onClick={() => setQuitando(c)}
                  >
                    <i className="bi bi-trash text-danger-ink" aria-hidden="true" />
                  </Button>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      <ConfirmDialog
        abierto={quitando != null}
        onCambio={(v) => { if (!v) setQuitando(null); }}
        titulo="Quitar de la ruta"
        descripcion={quitando ? `«${quitando.title}» sale de esta ruta. El curso en sí no se elimina, y el avance de quien ya lo hizo tampoco.` : ''}
        textoConfirmar="Quitar"
        destructivo
        cargando={quitar.isPending}
        onConfirmar={() => {
          if (!quitando) return;
          quitar.mutate(
            { id: ruta.id, lpc_id: quitando.lpc_id },
            {
              onSuccess: () => { setQuitando(null); Utils.toast('Curso quitado de la ruta', 'info'); },
              onError: (err) => {
                setQuitando(null);
                Utils.toast(err instanceof Error ? err.message : 'No se pudo quitar', 'error');
              },
            },
          );
        }}
      />
    </div>
  );
}

/* ── Las personas elegidas ─────────────────────────────────────────────── */

function Personas({ ruta }: { ruta: RutaDetalle }) {
  const personas = usePersonasDeLaRuta(ruta.id, true);
  const guardar = useGuardarPersonas();
  const lista = personas.data ?? [];

  function guardarLista(ids: number[], hecho: string) {
    guardar.mutate(
      { id: ruta.id, user_ids: ids },
      {
        onSuccess: () => Utils.toast(hecho, 'success'),
        onError: (err) => Utils.toast(err instanceof Error ? err.message : 'No se pudo guardar', 'error'),
      },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="rounded-sm border border-line bg-sunken p-3 text-2 text-ink-2">
        <i className="bi bi-info-circle mr-1.5 text-brand-ink" aria-hidden="true" />
        Si eliges personas aquí, la ruta es <strong>solo</strong> para ellas, sea cual sea su área,
        rol, cargo o empresa. Deja la lista vacía para volver a usar esos filtros.
      </p>

      {personas.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      ) : personas.isError ? (
        <EstadoError err={personas.error} que="las personas de la ruta" onReintentar={() => void personas.refetch()} />
      ) : (
        <>
          {/* Cada cambio manda la lista ENTERA, así que mientras se guarda uno
              no se admite otro: se calcularía sobre la lista de antes y lo
              desharía. El buscador sigue montado —si se quitara, el foco que
              devuelve al campo tras elegir se perdería— y lo dice. */}
          <BuscadorDePersonas
            id={`ruta-${ruta.id}-personas`}
            etiqueta="Buscar a una persona para asignarle la ruta"
            marcador="Escribe un nombre…"
            limite={6}
            excluir={lista.map((p) => p.id)}
            alElegir={(p) => {
              if (guardar.isPending) {
                Utils.toast(`Un momento: todavía se está guardando el cambio anterior. Vuelve a elegir a ${p.full_name}.`, 'info');
                return;
              }
              guardarLista([...lista.map((x) => x.id), p.id], `${p.full_name} añadida a la ruta`);
            }}
          />
          {guardar.isPending && (
            <p className="flex items-center gap-2 text-2 text-muted" role="status">
              <span className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />
              Guardando…
            </p>
          )}

          {!lista.length ? (
            <EstadoVacio
              icono="bi-people"
              titulo="Sin personas elegidas"
              descripcion="La ruta usa los filtros de área, rol, cargo y empresa."
              compacto
            />
          ) : (
            <ul aria-label="Personas elegidas" className="flex flex-col divide-y divide-line rounded-sm border border-line">
              {lista.map((p) => (
                <li key={p.id} className="flex items-center gap-2 px-3 py-2">
                  <span className="min-w-0 flex-1 truncate text-3">
                    {p.full_name}
                    {p.position && <span className="text-muted"> · {p.position}</span>}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    disabled={guardar.isPending}
                    aria-label={`Quitar a ${p.full_name}`}
                    onClick={() =>
                      guardarLista(lista.filter((x) => x.id !== p.id).map((x) => x.id), `${p.full_name} ya no está en la ruta`)
                    }
                  >
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
